/**
 * La correspondance du mode `?demo` (ARTO-130) : un dépôt « sale » (voir
 * `demoDepots.ts`), un lot profilé à corriger, un lot transformé en morceaux
 * qui cite l'ancienne version d'un profil, et la bibliothèque de profils. Tout
 * en mémoire, sans réseau, avec les codes de refus de l'API (`api.md`
 * § 12.16). Un lot créé ici avance avec le temps : profilage, transformation,
 * essai, lancement des morceaux. Aucune galerie ni personne réelle.
 */
import { ApiError } from './api.js';
import type {
  ApercuCorrespondance,
  ChoixCorrespondance,
  CiblesPivot,
  CreationLotDepot,
  GroupeMessage,
  NaturePivot,
  NouveauProfil,
  ProfilComplet,
  ProfilResume,
  ProfilSources,
  RapportEssaiMorceaux,
  RapportTransformation,
  Regles,
  Surcharges,
  VersionProfil,
} from './correspondance.js';
import { SURCHARGES_VIDES } from './correspondance.js';
import { CIBLES_DEMO } from './demoCibles.js';
import { demoApi } from './demoData.js';
import { demoDepots } from './demoDepots.js';
import {
  appliquerReglesDemo,
  reglesInvalidesDemo,
  verifierReglesDemo,
  type EntreeDemo,
} from './demoMoteur.js';
import { ID_PROFIL_EXPORT, profilsDeDepart } from './demoProfils.js';
import { lignesDeLEchantillon, profilerFichierDemo } from './demoProfilSources.js';
import type { CompteursLot, LotReprise, MorceauLot, NatureLot } from './reprise.js';
import { fichiersDesRoles, tableDeLaRegle } from './reglesEdition.js';

const LIGNES_PAR_MORCEAU = 5000;

export const il_y_a = (ms: number): string => new Date(Date.now() - ms).toISOString();

let compteur = 0;
export const nouvelId = (prefixe: string): string => `${prefixe}-demo-${String((compteur += 1))}`;

export function conflit(raison: string, message: string): ApiError {
  return new ApiError(409, 'conflit.etat', message, { raison });
}

export function introuvable(quoi: string): ApiError {
  return new ApiError(404, 'ressource.introuvable', `${quoi} introuvable.`);
}

// ── État ─────────────────────────────────────────────────────────────────

export interface LotCorr {
  vue: LotReprise;
  surcharges: Surcharges | null;
  transformation: RapportTransformation | null;
  /** Un morceau « à refus » : son essai trouvera des lignes refusées. */
  refus: boolean;
  phase: {
    genre: 'profilage' | 'transformation' | 'rapprochement' | 'essai' | 'import';
    depuis: number;
  } | null;
}

let profils: ProfilComplet[] | null = null;
const lotsParGalerie = new Map<string, LotCorr[]>();

function bibliotheque(): ProfilComplet[] {
  profils ??= profilsDeDepart();
  return profils;
}

function resume(profil: ProfilComplet): ProfilResume {
  const { regles: _regles, ...reste } = profil;
  return structuredClone(reste);
}

function derniere(id: string): ProfilComplet | undefined {
  return bibliotheque()
    .filter((profil) => profil.id === id)
    .sort((a, b) => b.version - a.version)[0];
}

function version(id: string, numero: number | undefined): ProfilComplet {
  const trouve =
    numero === undefined
      ? derniere(id)
      : bibliotheque().find((profil) => profil.id === id && profil.version === numero);
  if (!trouve) throw introuvable('Profil');
  return trouve;
}

function compteursVides(): CompteursLot {
  return { total: 0, crees: 0, completes: 0, ignores: 0, refuses: 0, ecartes: 0 };
}

export function lotVide(
  idGalerie: string,
  nature: NatureLot,
  sur: Partial<LotReprise> = {},
): LotReprise {
  return {
    id: nouvelId('lot'),
    galerieId: idGalerie,
    nature,
    etat: 'depose',
    revision: 1,
    sources: [],
    options: { confidentiels: false, motifInventaire: null },
    lignes: 0,
    compteurs: null,
    rapportEssai: false,
    rapport: false,
    travail: null,
    origine: 'depot',
    parcours: 'correspondance',
    depotId: null,
    parentId: null,
    morceau: null,
    profil: null,
    surcharges: false,
    profilage: null,
    transformation: null,
    morceaux: null,
    operateur: 'exploitation@venio.test',
    creeLe: new Date().toISOString(),
    essayeLe: null,
    lanceLe: null,
    termineLe: null,
    ...sur,
  };
}

export function travail(type: string, etat: string): NonNullable<LotReprise['travail']> {
  return {
    id: nouvelId('travail'),
    type,
    etat,
    tentatives: 1,
    maxTentatives: 3,
    termineLe: null,
    erreur: null,
  };
}

function idFichierDuDepot(idGalerie: string, depotId: string, chemin: string): string {
  const fichier = demoDepots.pageManifeste(idGalerie, depotId).find((f) => f.chemin === chemin);
  return fichier?.id ?? nouvelId('fichier');
}

export function morceauSemis(
  parent: LotReprise,
  nature: NatureLot,
  rang: number,
  lignes: number,
  etat: LotReprise['etat'],
  refuses = 0,
): LotCorr {
  const compteurs: CompteursLot | null =
    etat === 'depose'
      ? null
      : { total: lignes, crees: lignes - refuses, completes: 0, ignores: 0, refuses, ecartes: 0 };
  return {
    vue: lotVide(parent.galerieId, nature, {
      id: nouvelId('morceau'),
      origine: 'morceau',
      parcours: 'morceau',
      parentId: parent.id,
      morceau: { nature, rang },
      etat,
      sources: [{ champ: 'fichier', nom: `${nature}-${String(rang)}.csv`, octets: lignes * 96 }],
      lignes,
      compteurs,
      rapportEssai: etat !== 'depose',
      operateur: parent.operateur,
      creeLe: parent.creeLe,
      essayeLe: etat === 'depose' ? null : il_y_a(40 * 60_000),
    }),
    surcharges: null,
    transformation: null,
    refus: refuses > 0,
    phase: null,
  };
}

function semer(idGalerie: string, rang: number): LotCorr[] {
  if (rang !== 0) return [];
  const depot = demoDepots.depots(idGalerie)[0];
  if (!depot) return [];
  const oeuvres = idFichierDuDepot(idGalerie, depot.id, 'Export/Oeuvres.xlsx');
  const expositions = idFichierDuDepot(idGalerie, depot.id, 'Export/Expositions.csv');

  const transforme = lotVide(idGalerie, 'oeuvres', {
    etat: 'transforme',
    depotId: depot.id,
    sources: [{ champ: 'depot', nom: 'Export/Oeuvres.xlsx', octets: 912_384, fichierId: oeuvres }],
    lignes: 8412,
    profil: { id: ID_PROFIL_EXPORT, version: 1 },
    profilage: { fait: true },
    transformation: { fait: true, compteurs: null },
    creeLe: il_y_a(3 * 3_600_000),
  });
  const rapportTransformation: RapportTransformation = {
    lus: 8412,
    lignes: 8412,
    filtres: 392,
    natures: {
      oeuvres: { produites: 8212, filtres: 392, doublons: 0, vides: 0, refusees: 14 },
      artistes: { produites: 402, filtres: 0, doublons: 7810, vides: 0, refusees: 0 },
    },
    morceaux: [
      { nature: 'oeuvres', rang: 1, lignes: 5000, octets: 480_000 },
      { nature: 'oeuvres', rang: 2, lignes: 3212, octets: 308_352 },
      { nature: 'artistes', rang: 1, lignes: 402, octets: 38_592 },
    ],
    erreurs: [
      {
        fichier: 'Export/Oeuvres.xlsx',
        ligne: 140,
        nature: 'oeuvres',
        colonne: 'Prix public (EUR)',
        message: 'Le prix « sur demande » n’est pas un montant.',
      },
      {
        fichier: 'Export/Oeuvres.xlsx',
        ligne: 1873,
        nature: 'oeuvres',
        colonne: 'Titre',
        message: 'La colonne « Titre » est obligatoire.',
      },
      {
        fichier: 'Export/Oeuvres.xlsx',
        ligne: 5120,
        nature: 'oeuvres',
        colonne: 'Prix public (EUR)',
        message: 'Le prix « sur demande » n’est pas un montant.',
      },
    ],
    erreursTotal: 14,
    avertissements: [],
    avertissementsTotal: 0,
  };
  const corr: LotCorr[] = [
    {
      vue: transforme,
      surcharges: null,
      transformation: rapportTransformation,
      refus: false,
      phase: null,
    },
  ];
  corr.push(
    morceauSemis(transforme, 'oeuvres', 1, 5000, 'pret'),
    morceauSemis(transforme, 'oeuvres', 2, 3212, 'essaye', 14),
    morceauSemis(transforme, 'artistes', 1, 402, 'depose'),
  );

  const profile = lotVide(idGalerie, 'expositions', {
    etat: 'profile',
    depotId: depot.id,
    sources: [
      { champ: 'depot', nom: 'Export/Expositions.csv', octets: 9_216, fichierId: expositions },
    ],
    lignes: 64,
    profilage: { fait: true },
    creeLe: il_y_a(40 * 60_000),
  });
  corr.unshift({
    vue: profile,
    surcharges: null,
    transformation: null,
    refus: false,
    phase: null,
  });

  // Un export Mailchimp des désabonnés : le profilage propose « Mailchimp
  // (désabonnés) » avant « Mailchimp (abonnés) », dont la signature est incluse.
  const mailchimp = idFichierDuDepot(idGalerie, depot.id, 'Export/Mailchimp-desabonnes.csv');
  corr.unshift({
    vue: lotVide(idGalerie, 'contacts', {
      etat: 'profile',
      depotId: depot.id,
      sources: [
        {
          champ: 'depot',
          nom: 'Export/Mailchimp-desabonnes.csv',
          octets: 31_744,
          fichierId: mailchimp,
        },
      ],
      lignes: 318,
      profilage: { fait: true },
      creeLe: il_y_a(15 * 60_000),
    }),
    surcharges: null,
    transformation: null,
    refus: false,
    phase: null,
  });
  return corr;
}

export function lotsDe(idGalerie: string): LotCorr[] {
  const connu = lotsParGalerie.get(idGalerie);
  if (connu) return connu;
  const rang = demoApi.listerGaleries().findIndex((galerie) => galerie.id === idGalerie);
  const nouveau = [...(crochetsDemo.semer?.(idGalerie, rang) ?? []), ...semer(idGalerie, rang)];
  lotsParGalerie.set(idGalerie, nouveau);
  return nouveau;
}

export function trouver(idGalerie: string, lotId: string): LotCorr {
  const lot = lotsDe(idGalerie).find((candidat) => candidat.vue.id === lotId);
  if (!lot) throw introuvable('Lot');
  avancer(idGalerie, lot);
  return lot;
}

export function enfants(idGalerie: string, parent: LotCorr): LotCorr[] {
  return lotsDe(idGalerie).filter(
    (lot) => lot.vue.parentId === parent.vue.id && lot.vue.etat !== 'annule',
  );
}

// ── Le temps qui passe ───────────────────────────────────────────────────

/**
 * Ce que `demoImages` sait faire à l'arrivée d'un rapprochement : il pose la
 * revue et fabrique les morceaux. Posé par ce module au chargement.
 */
export const crochetsDemo: {
  rapproche?: (idGalerie: string, lot: LotCorr) => void;
  /** Les lots d'images de départ, à la suite de ceux de la correspondance. */
  semer?: (idGalerie: string, rang: number) => LotCorr[];
} = {};

function profilDuLot(lot: LotCorr): ProfilComplet | null {
  const ref = lot.vue.profil;
  return ref ? version(ref.id, ref.version) : null;
}

function fabriquerMorceaux(idGalerie: string, parent: LotCorr): void {
  const profil = profilDuLot(parent);
  if (!profil) return;
  const lus = parent.vue.lignes;
  const natures: RapportTransformation['natures'] = {};
  const morceaux: RapportTransformation['morceaux'] = [];
  for (const nature of Object.keys(profil.regles.natures)) {
    const principale = nature === parent.vue.nature;
    const produites = principale ? Math.round(lus * 0.97) : Math.max(1, Math.round(lus / 20));
    natures[nature] = {
      produites,
      filtres: principale ? lus - produites : 0,
      doublons: principale ? 0 : lus - produites,
      vides: 0,
      refusees: 0,
    };
    for (let debut = 0, rang = 1; debut < produites; debut += LIGNES_PAR_MORCEAU, rang += 1) {
      const lignes = Math.min(LIGNES_PAR_MORCEAU, produites - debut);
      morceaux.push({ nature, rang, lignes, octets: lignes * 96 });
      lotsDe(idGalerie).push(
        morceauSemis(parent.vue, nature as NaturePivot, rang, lignes, 'depose'),
      );
    }
  }
  const apercu = apercuDuLot(parent, profil.regles, parent.surcharges ?? SURCHARGES_VIDES);
  parent.transformation = {
    lus,
    lignes: lus,
    filtres: natures[parent.vue.nature]?.filtres ?? 0,
    natures,
    morceaux,
    erreurs: apercu.erreurs,
    erreursTotal: apercu.erreurs.length * 7,
    avertissements: [],
    avertissementsTotal: 0,
  };
}

function rapportEssaiDuMorceau(lot: LotCorr): object {
  const refuses = lot.vue.compteurs?.refuses ?? 0;
  return {
    essai: true,
    total: lot.vue.lignes,
    crees: lot.vue.lignes - refuses,
    completes: 0,
    ignores: 0,
    refuses,
    colonnesInconnues: [],
    erreurs: lignesRefusees(lot),
    avertissements: [],
    details: {},
    listes: {},
  };
}

function lignesRefusees(lot: LotCorr): { ligne: number; libelle: string; messages: string[] }[] {
  const refuses = lot.vue.compteurs?.refuses ?? 0;
  const titres = [
    'Étude pour un paysage',
    'Nature morte aux coings',
    'Fenêtre sur cour',
    'Hiver blanc',
  ];
  return Array.from({ length: Math.min(refuses, 14) }, (_, i) => ({
    ligne: 140 + i * 37,
    libelle: titres[i % titres.length] ?? '',
    messages: [
      i % 3 === 2
        ? 'La colonne « Titre » est obligatoire.'
        : 'Le prix « sur demande » n’est pas un montant.',
    ],
  }));
}

function avancer(idGalerie: string, lot: LotCorr): void {
  const phase = lot.phase;
  if (!phase) return;
  const ecoule = Date.now() - phase.depuis;
  const termine = (): void => {
    if (lot.vue.travail) {
      lot.vue.travail.etat = 'reussi';
      lot.vue.travail.termineLe = new Date().toISOString();
    }
    lot.vue.revision += 1;
    lot.phase = null;
  };
  if (phase.genre === 'profilage' && ecoule >= 1200) {
    lot.vue.profilage = { fait: true };
    lot.vue.etat = 'profile';
    lot.vue.lignes = lot.vue.sources.reduce((somme, source) => {
      const profil = profilerFichierDemo(source.fichierId ?? '', source.nom, source.octets);
      return somme + (profil.tables?.[0]?.lignes ?? 0);
    }, 0);
    termine();
  } else if (phase.genre === 'transformation' && ecoule >= 1500) {
    lot.vue.etat = 'transforme';
    lot.vue.transformation = { fait: true, compteurs: null };
    fabriquerMorceaux(idGalerie, lot);
    termine();
  } else if (phase.genre === 'rapprochement' && ecoule >= 1500) {
    lot.vue.etat = 'rapproche';
    crochetsDemo.rapproche?.(idGalerie, lot);
    termine();
  } else if (phase.genre === 'essai' && ecoule >= 1500) {
    const refuses = lot.refus ? Math.max(1, Math.round(lot.vue.lignes / 230)) : 0;
    lot.vue.compteurs = {
      total: lot.vue.lignes,
      crees: lot.vue.lignes - refuses,
      completes: 0,
      ignores: 0,
      refuses,
      ecartes: 0,
    };
    lot.vue.etat = refuses > 0 ? 'essaye' : 'pret';
    lot.vue.rapportEssai = true;
    lot.vue.essayeLe = new Date().toISOString();
    termine();
  } else if (phase.genre === 'import') {
    if (ecoule < 1500) return;
    if (ecoule < 5000) {
      lot.vue.etat = 'en_cours';
      if (lot.vue.travail) lot.vue.travail.etat = 'en_cours';
      return;
    }
    lot.vue.etat = 'termine';
    lot.vue.rapport = true;
    lot.vue.termineLe = new Date().toISOString();
    termine();
  }
}

// ── Vues ─────────────────────────────────────────────────────────────────

function vue(idGalerie: string, lot: LotCorr): LotReprise {
  avancer(idGalerie, lot);
  const copie = structuredClone(lot.vue);
  copie.surcharges = lot.surcharges !== null;
  if (copie.origine === 'depot') {
    const liste: MorceauLot[] = enfants(idGalerie, lot).map((morceau) => {
      avancer(idGalerie, morceau);
      return {
        id: morceau.vue.id,
        nature: morceau.vue.nature,
        rang: morceau.vue.morceau?.rang ?? 0,
        lignes: morceau.vue.lignes,
        etat: morceau.vue.etat,
        compteurs: morceau.vue.compteurs,
      };
    });
    const etats: Record<string, number> = {};
    const compteurs = compteursVides();
    for (const morceau of liste) {
      etats[morceau.etat] = (etats[morceau.etat] ?? 0) + 1;
      if (morceau.compteurs)
        for (const cle of Object.keys(compteurs) as (keyof CompteursLot)[])
          compteurs[cle] = (compteurs[cle] ?? 0) + (morceau.compteurs[cle] ?? 0);
    }
    copie.morceaux = {
      morceaux: liste.length,
      etats,
      compteurs,
      pret: liste.length > 0 && liste.every((morceau) => morceau.etat === 'pret'),
      liste,
    };
  }
  return copie;
}

function profilSourcesDuLot(lot: LotCorr): ProfilSources {
  if (lot.vue.profilage?.fait !== true)
    throw conflit('lot_non_profile', 'Ce lot n’est pas encore profilé.');
  return {
    version: 1,
    lotId: lot.vue.id,
    etabliLe: lot.vue.creeLe,
    fichiers: lot.vue.sources.map((source) =>
      profilerFichierDemo(source.fichierId ?? '', source.nom, source.octets),
    ),
  };
}

function apercuDuLot(lot: LotCorr, regles: Regles, surcharges: Surcharges): ApercuCorrespondance {
  const profil = profilSourcesDuLot(lot);
  const entrees: Record<string, EntreeDemo | undefined> = {};
  const fichiers = fichiersDesRoles(regles, lot.vue.sources, surcharges);
  for (const source of regles.sources) {
    const fichierId = fichiers[source.role] ?? null;
    const table = tableDeLaRegle(profil, fichierId, source);
    const fichier = profil.fichiers.find((candidat) => candidat.fichierId === fichierId);
    if (!table || !fichier)
      throw conflit(
        'role_sans_fichier',
        `Aucun fichier du lot ne joue le rôle « ${source.role} ».`,
      );
    entrees[source.role] = { fichier: fichier.chemin, lignes: lignesDeLEchantillon(table) };
  }
  return appliquerReglesDemo(regles, surcharges, entrees, CIBLES_DEMO.natures);
}

function groupe(erreurs: { message: string; occurrence: GroupeMessage['occurrences'][number] }[]) {
  const groupes = new Map<string, GroupeMessage>();
  for (const { message, occurrence } of erreurs) {
    const courant = groupes.get(message) ?? { message, nombre: 0, occurrences: [] };
    courant.nombre += 1;
    if (courant.occurrences.length < 50) courant.occurrences.push(occurrence);
    groupes.set(message, courant);
  }
  return [...groupes.values()].sort((a, b) => b.nombre - a.nombre);
}

function csvDuPivot(lot: LotCorr, cible: { nature: NaturePivot; morceau?: number }): string {
  const profil = profilDuLot(lot);
  const colonnes =
    CIBLES_DEMO.natures.find((candidat) => candidat.nature === cible.nature)?.colonnes ?? [];
  if (!profil) return `${colonnes.map((colonne) => colonne.entete).join(';')}\n`;
  const apercu = apercuDuLot(lot, profil.regles, lot.surcharges ?? SURCHARGES_VIDES);
  const sortie = apercu.natures[cible.nature];
  const lignes = (sortie?.lignes ?? []).map((ligne) =>
    colonnes.map((colonne) => ligne[colonne.cle] ?? '').join(';'),
  );
  return `\ufeff${[colonnes.map((colonne) => colonne.entete).join(';'), ...lignes].join('\r\n')}\r\n`;
}

// ── Interface ────────────────────────────────────────────────────────────

export const demoCorrespondance = {
  /** Ce lot (ou ce morceau) est-il géré ici ? */
  possede(idGalerie: string, lotId: string): boolean {
    return lotsDe(idGalerie).some((lot) => lot.vue.id === lotId);
  },

  /** Les lots de premier niveau. */
  lots(idGalerie: string): LotReprise[] {
    return lotsDe(idGalerie)
      .filter((lot) => lot.vue.parentId === null)
      .map((lot) => vue(idGalerie, lot));
  },

  lot(idGalerie: string, lotId: string): LotReprise {
    return vue(idGalerie, trouver(idGalerie, lotId));
  },

  creerLot(idGalerie: string, corps: CreationLotDepot): LotReprise {
    const depot = demoDepots.depots(idGalerie).find((candidat) => candidat.id === corps.depotId);
    if (!depot) throw introuvable('Dépôt');
    if (!depot.utilisable)
      throw conflit(
        'depot_inutilisable',
        'Ce dépôt n’est pas utilisable : son inventaire n’est pas fait, ou un fichier attend l’antivirus.',
      );
    const manifeste = demoDepots.pageManifeste(idGalerie, corps.depotId);
    const sources = corps.fichiers.map((id) => {
      const fichier = manifeste.find((candidat) => candidat.id === id);
      if (!fichier || fichier.etat !== 'accepte')
        throw conflit('fichier_inutilisable', 'Un fichier cité n’est pas accepté dans ce dépôt.');
      return { champ: 'depot', nom: fichier.chemin, octets: fichier.octets, fichierId: id };
    });
    const lot: LotCorr = {
      vue: lotVide(idGalerie, corps.nature, { depotId: corps.depotId, sources }),
      surcharges: null,
      transformation: null,
      refus: false,
      phase: null,
    };
    lotsDe(idGalerie).unshift(lot);
    return vue(idGalerie, lot);
  },

  profiler(idGalerie: string, lotId: string): LotReprise {
    const lot = trouver(idGalerie, lotId);
    if (lot.phase) throw conflit('travail_en_cours', 'Un travail de ce lot est déjà en cours.');
    lot.vue.profilage = { fait: false };
    lot.vue.travail = travail('reprise:profiler', 'en_attente');
    lot.vue.revision += 1;
    lot.phase = { genre: 'profilage', depuis: Date.now() };
    return vue(idGalerie, lot);
  },

  transformer(idGalerie: string, lotId: string): LotReprise {
    const lot = trouver(idGalerie, lotId);
    if (lot.phase) throw conflit('travail_en_cours', 'Un travail de ce lot est déjà en cours.');
    if (lot.vue.profil == null)
      throw conflit(
        'profil_requis',
        'Choisissez un profil de correspondance avant de transformer.',
      );
    exigerMorceauxLibres(idGalerie, lot);
    for (const morceau of enfants(idGalerie, lot)) morceau.vue.etat = 'annule';
    lot.vue.transformation = { fait: false, compteurs: null };
    lot.vue.etat = lot.vue.profilage?.fait ? 'profile' : 'depose';
    lot.vue.travail = travail('reprise:transformer', 'en_attente');
    lot.vue.revision += 1;
    lot.phase = { genre: 'transformation', depuis: Date.now() };
    return vue(idGalerie, lot);
  },

  choisir(idGalerie: string, lotId: string, corps: ChoixCorrespondance): LotReprise {
    const lot = trouver(idGalerie, lotId);
    if (corps.revision !== lot.vue.revision)
      throw conflit('lot_modifie', 'Le lot vient de changer : rechargez.');
    exigerMorceauxLibres(idGalerie, lot);
    let regles: Regles | null = profilDuLot(lot)?.regles ?? null;
    if (corps.profil !== undefined) {
      if (corps.profil === null) {
        lot.vue.profil = null;
        lot.surcharges = null;
        regles = null;
      } else {
        const profil = version(corps.profil.id, corps.profil.version);
        if (profil.nature !== lot.vue.nature)
          throw new ApiError(
            400,
            'validation.invalide',
            `Ce profil alimente les ${profil.nature}, ce lot les ${lot.vue.nature}.`,
            { raison: 'profil_hors_nature' },
          );
        lot.vue.profil = { id: profil.id, version: profil.version };
        // Un nouveau profil retire les surcharges, sauf si elles sont renvoyées.
        lot.surcharges = null;
        regles = profil.regles;
      }
    }
    if (corps.surcharges !== undefined) {
      if (corps.surcharges === null) lot.surcharges = null;
      else {
        if (regles === null)
          throw conflit(
            'profil_requis',
            'Des surcharges s’appliquent à un profil : choisissez-le.',
          );
        const champs = surchargesInvalides(corps.surcharges, regles, lot);
        if (champs.length > 0) throw reglesInvalidesDemo(champs);
        lot.surcharges = structuredClone(corps.surcharges);
      }
    }
    for (const morceau of enfants(idGalerie, lot)) morceau.vue.etat = 'annule';
    lot.vue.transformation = null;
    lot.vue.etat = lot.vue.profilage?.fait ? 'profile' : 'depose';
    lot.vue.revision += 1;
    return vue(idGalerie, lot);
  },

  profilSources(idGalerie: string, lotId: string): ProfilSources {
    return profilSourcesDuLot(trouver(idGalerie, lotId));
  },

  surcharges(idGalerie: string, lotId: string): Surcharges | null {
    const lot = trouver(idGalerie, lotId);
    return lot.surcharges === null ? null : structuredClone(lot.surcharges);
  },

  transformation(idGalerie: string, lotId: string): RapportTransformation {
    const lot = trouver(idGalerie, lotId);
    if (!lot.transformation) throw introuvable('Ce lot n’est pas transformé. Rapport');
    return structuredClone(lot.transformation);
  },

  apercu(
    idGalerie: string,
    lotId: string,
    corps: { regles: Regles; surcharges: Surcharges },
  ): ApercuCorrespondance {
    const lot = trouver(idGalerie, lotId);
    profilSourcesDuLot(lot);
    const erreurs = verifierReglesDemo(corps.regles, lot.vue.nature, CIBLES_DEMO.natures);
    if (erreurs.length > 0) throw reglesInvalidesDemo(erreurs);
    return apercuDuLot(lot, corps.regles, corps.surcharges);
  },

  essayer(idGalerie: string, lotId: string): LotReprise {
    const lot = trouver(idGalerie, lotId);
    if (lot.vue.parcours === 'images') {
      if ((lot.vue.rapprochement?.compteurs?.ambigus ?? 0) > 0)
        throw conflit(
          'ambigus_a_trancher',
          'Des fichiers sont ambigus : tranchez-les (rattacher ou ignorer), puis rapprochez de nouveau.',
        );
      if (lot.vue.etat !== 'rapproche')
        throw conflit(
          'lot_non_transforme',
          'Ce lot d’images se rapproche des œuvres avant de s’essayer.',
        );
    }
    if (lot.vue.origine === 'depot') {
      if (lot.vue.parcours !== 'images' && lot.vue.etat !== 'transforme')
        throw conflit(
          'lot_non_transforme',
          'Ce lot vient d’un dépôt : transformez-le en pivot avant de l’essayer.',
        );
      const libres = enfants(idGalerie, lot).filter((morceau) =>
        ['depose', 'essaye', 'pret'].includes(morceau.vue.etat),
      );
      if (libres.length === 0)
        throw conflit('rien_a_essayer', 'Aucun morceau de ce lot n’est à essayer.');
      for (const morceau of libres) lancerEssai(morceau);
      return vue(idGalerie, lot);
    }
    if (!['depose', 'essaye', 'pret'].includes(lot.vue.etat))
      throw conflit('lot_deja_lance', 'Ce lot est déjà lancé : il ne s’essaie plus.');
    if (lot.phase?.genre === 'essai')
      throw conflit('essai_en_cours', 'Un essai de ce lot est déjà en cours.');
    lancerEssai(lot);
    return vue(idGalerie, lot);
  },

  lancer(idGalerie: string, lotId: string): LotReprise {
    const lot = trouver(idGalerie, lotId);
    if (lot.vue.origine === 'depot')
      throw conflit(
        'lot_de_correspondance',
        'Ce lot vient d’un dépôt : ses morceaux se lancent un par un.',
      );
    if (lot.vue.etat !== 'pret')
      throw conflit(
        'lot_non_pret',
        'Le lot doit avoir réussi son essai, sans refus, avant d’être lancé.',
      );
    lot.vue.etat = 'enfile';
    lot.vue.lanceLe = new Date().toISOString();
    lot.vue.revision += 1;
    lot.vue.travail = travail(`import:${lot.vue.nature}`, 'en_attente');
    lot.phase = { genre: 'import', depuis: Date.now() };
    return vue(idGalerie, lot);
  },

  /** Le rapport d'un morceau : l'essai, ou l'import terminé. */
  rapport(idGalerie: string, lotId: string, type: 'essai' | 'final'): object {
    const lot = trouver(idGalerie, lotId);
    if (type === 'final') {
      if (!lot.vue.rapport) throw introuvable('Rapport');
      return {
        essai: false,
        total: lot.vue.lignes,
        crees: lot.vue.lignes,
        completes: 0,
        ignores: 0,
        refuses: 0,
        colonnesInconnues: [],
        erreurs: [],
        avertissements: [],
        details: {},
        listes: {},
      };
    }
    if (!lot.vue.rapportEssai) throw introuvable('Rapport');
    return rapportEssaiDuMorceau(lot);
  },

  rapportEssai(idGalerie: string, lotId: string): RapportEssaiMorceaux {
    const parent = trouver(idGalerie, lotId);
    const morceaux = enfants(idGalerie, parent);
    const essayes = morceaux.filter((morceau) => morceau.vue.rapportEssai);
    const totaux = { total: 0, crees: 0, completes: 0, ignores: 0, refuses: 0 };
    const erreurs: Parameters<typeof groupe>[0] = [];
    for (const morceau of essayes) {
      avancer(idGalerie, morceau);
      const compteurs = morceau.vue.compteurs;
      if (compteurs)
        for (const cle of Object.keys(totaux) as (keyof typeof totaux)[])
          totaux[cle] += compteurs[cle];
      for (const ligne of lignesRefusees(morceau))
        for (const message of ligne.messages)
          erreurs.push({
            message,
            occurrence: {
              morceau: morceau.vue.morceau ?? null,
              lotId: morceau.vue.id,
              ligne: ligne.ligne,
              libelle: ligne.libelle,
            },
          });
    }
    return {
      lotId,
      morceaux: morceaux.length,
      essayes: essayes.length,
      ...totaux,
      erreurs: groupe(erreurs),
      avertissements: [],
    };
  },

  pivot(
    idGalerie: string,
    lotId: string,
    cible: { nature: NaturePivot; morceau?: number },
  ): string {
    return csvDuPivot(trouver(idGalerie, lotId), cible);
  },

  // ── Bibliothèque de profils ────────────────────────────────────────────

  cibles(): CiblesPivot {
    return structuredClone(CIBLES_DEMO);
  },

  profils(nature?: NaturePivot): ProfilResume[] {
    const ids = [...new Set(bibliotheque().map((profil) => profil.id))];
    return ids
      .map((id) => derniere(id))
      .filter((profil): profil is ProfilComplet => profil !== undefined)
      .filter((profil) => nature === undefined || profil.nature === nature)
      .sort((a, b) => a.nom.localeCompare(b.nom, 'fr'))
      .map(resume);
  },

  profil(id: string, numero?: number): ProfilComplet {
    return structuredClone(version(id, numero));
  },

  versions(id: string): ProfilResume[] {
    const liste = bibliotheque()
      .filter((profil) => profil.id === id)
      .sort((a, b) => b.version - a.version);
    if (liste.length === 0) throw introuvable('Profil');
    return liste.map(resume);
  },

  creerProfil(corps: NouveauProfil): ProfilComplet {
    const champs = verifierReglesDemo(corps.regles, corps.nature, CIBLES_DEMO.natures);
    if (champs.length > 0) throw reglesInvalidesDemo(champs);
    const cree: ProfilComplet = {
      id: nouvelId('profil'),
      version: 1,
      nom: corps.nom,
      application: corps.application,
      nature: corps.nature,
      natures: Object.keys(corps.regles.natures),
      signature: corps.signature,
      fourni: false,
      auteur: 'exploitation@venio.test',
      creeLe: new Date().toISOString(),
      regles: structuredClone(corps.regles),
    };
    bibliotheque().push(cree);
    return structuredClone(cree);
  },

  publierVersion(id: string, corps: VersionProfil): ProfilComplet {
    const derniereVersion = version(id, undefined);
    if (corps.version !== derniereVersion.version)
      throw new ApiError(
        409,
        'conflit.version',
        'Une version plus récente de ce profil a été publiée : rechargez.',
        { raison: 'version_perimee', version: derniereVersion.version },
      );
    const champs = verifierReglesDemo(corps.regles, derniereVersion.nature, CIBLES_DEMO.natures);
    if (champs.length > 0) throw reglesInvalidesDemo(champs);
    const suivante: ProfilComplet = {
      ...derniereVersion,
      version: derniereVersion.version + 1,
      nom: corps.nom,
      application: corps.application,
      signature: corps.signature,
      natures: Object.keys(corps.regles.natures),
      auteur: 'exploitation@venio.test',
      creeLe: new Date().toISOString(),
      regles: structuredClone(corps.regles),
    };
    bibliotheque().push(suivante);
    return structuredClone(suivante);
  },
};

function lancerEssai(lot: LotCorr): void {
  lot.vue.etat = 'depose';
  lot.vue.compteurs = null;
  lot.vue.rapportEssai = false;
  lot.vue.revision += 1;
  lot.vue.travail = travail('reprise:essayer', 'en_attente');
  lot.phase = { genre: 'essai', depuis: Date.now() };
}

function exigerMorceauxLibres(idGalerie: string, lot: LotCorr): void {
  const lance = enfants(idGalerie, lot).some((morceau) =>
    ['enfile', 'en_cours', 'termine', 'echoue', 'scelle'].includes(morceau.vue.etat),
  );
  if (lance)
    throw conflit(
      'morceaux_lances',
      'Un morceau de ce lot est déjà lancé : déposez un nouveau lot pour reprendre autrement.',
    );
}

function surchargesInvalides(
  surcharges: Surcharges,
  regles: Regles,
  lot: LotCorr,
): { chemin: string; message: string }[] {
  const erreurs: { chemin: string; message: string }[] = [];
  const roles = regles.sources.map((source) => source.role);
  for (const [role, fichierId] of Object.entries(surcharges.fichiers)) {
    if (!roles.includes(role))
      erreurs.push({
        chemin: `fichiers.${role}`,
        message: `« ${role} » n’est pas une source du profil.`,
      });
    else if (!lot.vue.sources.some((source) => source.fichierId === fichierId))
      erreurs.push({
        chemin: `fichiers.${role}`,
        message: 'Ce fichier n’est pas une source du lot.',
      });
  }
  const champsConnus = new Set(
    Object.entries(regles.natures).flatMap(([nature, bloc]) =>
      Object.keys(bloc.colonnes).map((cle) => `${nature}.${cle}`),
    ),
  );
  for (const cle of [
    ...Object.keys(surcharges.dictionnaires),
    ...Object.keys(surcharges.constantes),
  ])
    if (!champsConnus.has(cle))
      erreurs.push({ chemin: cle, message: `« ${cle} » n’est pas un champ des règles du profil.` });
  return erreurs;
}
